import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, UserRound } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { FormSelect } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { PasswordInput } from '@/features/account/PasswordField'
import { useAuth } from '@/lib/auth/AuthProvider'
import {
  useCreateAccessUser,
  useDirectoryUser,
  useLinkableEmployees,
  useMailProviders,
  useRoleOptions,
  useUpdateAccessUser,
} from '../api'
import { MAIL_ACCOUNT_MANAGE_PERM } from '../permissions'
import type { DirectoryUserRow, MailboxUpdateInput } from '../types'
import { toastError } from '../ui'
import { MailboxPanel } from './MailboxPanel'
import { RoleChecklist } from './RoleChecklist'

type Props = { open: boolean; onOpenChange: (v: boolean) => void; user: DirectoryUserRow | null }

const sameIds = (a: string[], b: string[]) => {
  const set = new Set(b)
  return new Set(a).size === set.size && a.every((id) => set.has(id))
}

export function UserFormDialog({ open, onOpenChange, user }: Props) {
  return open ? <UserForm key={user?.id ?? 'new'} user={user} onDone={() => onOpenChange(false)} /> : null
}

function UserForm({ user, onDone }: { user: DirectoryUserRow | null; onDone: () => void }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const create = useCreateAccessUser()
  const update = useUpdateAccessUser()
  const roles = useRoleOptions().data ?? []
  const canMailbox = can(MAIL_ACCOUNT_MANAGE_PERM)
  const employees = useLinkableEmployees(!user).data ?? []
  const providers = useMailProviders(canMailbox).data ?? []
  const detail = useDirectoryUser(user?.id ?? '', !!user && canMailbox)
  const editing = detail.data ?? user
  const mailbox = editing?.mailbox ?? null
  const pending = create.isPending || update.isPending
  const [mailboxOn, setMailboxOn] = useState(false)
  const [mailboxRemove, setMailboxRemove] = useState(false)

  const defaultProviderId = useMemo(() => {
    const def = providers.find((p) => p.isDefault) ?? providers[0]
    return def?.id ?? ''
  }, [providers])

  const schema = useMemo(
    () =>
      z
        .object({
          firstName: z.string().trim(),
          lastName: z.string().trim(),
          email: z.string().trim(),
          password: z.string(),
          roleIds: z.array(z.string()).min(1, t('access.directory.form.roleRequired')),
          employeeId: user ? z.string() : z.string().min(1, t('access.directory.form.employeeRequired')),
          mailboxProviderId: z.string(),
          mailboxSecret: z.string(),
          mailboxSignature: z.string(),
          mailboxEmail: z.string(),
        })
        .superRefine((v, ctx) => {
          if (user) {
            if (v.firstName.trim().length < 2) {
              ctx.addIssue({ code: 'custom', path: ['firstName'], message: t('access.directory.form.firstNameRequired') })
            }
            if (v.lastName.trim().length < 2) {
              ctx.addIssue({ code: 'custom', path: ['lastName'], message: t('access.directory.form.lastNameRequired') })
            }
          }
          if (!user && v.password.trim() && v.password.trim().length < 8) {
            ctx.addIssue({
              code: 'custom',
              path: ['password'],
              message: t('access.directory.form.loginPasswordMin'),
            })
          }
          if (!canMailbox || mailboxRemove) return
          const assigning = !user ? mailboxOn : !mailbox && mailboxOn
          const editingBox = !!user && !!mailbox && !mailboxRemove
          if (assigning) {
            if (!v.mailboxProviderId) {
              ctx.addIssue({
                code: 'custom',
                path: ['mailboxProviderId'],
                message: t('access.directory.form.mailboxProviderRequired'),
              })
            }
            if (!v.mailboxSecret.trim()) {
              ctx.addIssue({
                code: 'custom',
                path: ['mailboxSecret'],
                message: t('access.directory.form.mailboxSecretRequired'),
              })
            }
          }
          if (editingBox && v.mailboxEmail.trim() && !z.string().email().safeParse(v.mailboxEmail.trim()).success) {
            ctx.addIssue({
              code: 'custom',
              path: ['mailboxEmail'],
              message: t('access.directory.form.emailInvalid'),
            })
          }
        }),
    [t, user, canMailbox, mailboxOn, mailbox, mailboxRemove],
  )

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      email: user?.email ?? '',
      password: '',
      roleIds: user?.roleIds ?? [],
      employeeId: user?.employeeId ?? '',
      mailboxProviderId: '',
      mailboxSecret: '',
      mailboxSignature: '',
      mailboxEmail: '',
    },
  })
  const { errors } = form.formState
  const employeeId = useWatch({ control: form.control, name: 'employeeId' })
  const selectedEmployee = useMemo(() => employees.find((e) => e.id === employeeId), [employees, employeeId])

  useEffect(() => {
    if (!editing || !user) return
    form.reset({
      firstName: editing.firstName,
      lastName: editing.lastName,
      email: editing.email,
      password: '',
      roleIds: editing.roleIds,
      employeeId: editing.employeeId ?? '',
      mailboxProviderId: editing.mailbox?.providerId || defaultProviderId,
      mailboxSecret: '',
      mailboxSignature: editing.mailbox?.signatureHtml ?? '',
      mailboxEmail: editing.mailbox?.email || editing.email,
    })
    setMailboxOn(false)
    setMailboxRemove(false)
  }, [editing, user, form, defaultProviderId])

  useEffect(() => {
    if (user || !selectedEmployee) return
    form.setValue('email', selectedEmployee.workEmail)
    const parts = selectedEmployee.name.trim().split(/\s+/)
    if (parts.length >= 2) {
      form.setValue('firstName', parts[0] ?? '')
      form.setValue('lastName', parts.slice(1).join(' '))
    }
  }, [selectedEmployee, user, form])

  useEffect(() => {
    if (!user && defaultProviderId && !form.getValues('mailboxProviderId')) {
      form.setValue('mailboxProviderId', defaultProviderId)
    }
  }, [defaultProviderId, user, form])

  const buildMailboxUpdate = (v: {
    mailboxProviderId: string
    mailboxSecret: string
    mailboxSignature: string
    mailboxEmail: string
  }): MailboxUpdateInput | undefined => {
    if (!canMailbox) return undefined
    if (mailboxRemove && mailbox) return { remove: true }
    if (mailbox && !mailboxRemove) {
      const patch: MailboxUpdateInput = { remove: false }
      const email = v.mailboxEmail.trim()
      if (email && email !== mailbox.email) patch.email = email
      if (v.mailboxSecret.trim()) patch.secret = v.mailboxSecret.trim()
      if (v.mailboxSignature !== mailbox.signatureHtml) patch.signatureHtml = v.mailboxSignature
      if (!('email' in patch) && !('secret' in patch) && !('signatureHtml' in patch)) return undefined
      return patch
    }
    if (!mailbox && mailboxOn && v.mailboxProviderId && v.mailboxSecret.trim()) {
      return {
        providerId: v.mailboxProviderId,
        secret: v.mailboxSecret.trim(),
        signatureHtml: v.mailboxSignature.trim() || undefined,
        email: v.mailboxEmail.trim() || undefined,
      }
    }
    return undefined
  }

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      if (user) {
        await update.mutateAsync({
          id: user.id,
          firstName: v.firstName,
          lastName: v.lastName,
          // Roller değişmediyse gönderilmez: rol ataması kullanıcının oturumunu 30 sn sonra kapatır.
          roleIds: sameIds(v.roleIds, user.roleIds) ? undefined : v.roleIds,
          mailbox: buildMailboxUpdate(v),
        })
        toast.success(t('access.directory.updated'))
      } else {
        await create.mutateAsync({
          employeeId: v.employeeId,
          roleIds: v.roleIds,
          email: selectedEmployee?.workEmail || v.email || undefined,
          firstName: v.firstName || undefined,
          lastName: v.lastName || undefined,
          password: v.password.trim() || undefined,
          mailbox:
            canMailbox && mailboxOn && v.mailboxProviderId && v.mailboxSecret.trim()
              ? {
                  providerId: v.mailboxProviderId,
                  secret: v.mailboxSecret.trim(),
                  signatureHtml: v.mailboxSignature.trim() || undefined,
                }
              : undefined,
        })
        toast.success(t('access.directory.created'))
      }
      onDone()
    } catch (e) {
      toastError(e)
    }
  })

  const showMailbox = canMailbox && (!user || !detail.isLoading)

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onDone()}
      dismissible={!pending}
      size="xl"
      icon={<UserRound />}
      title={t(user ? 'access.directory.form.editTitle' : 'access.directory.form.createTitle')}
      description={t(user ? 'access.directory.form.editHint' : 'access.directory.form.createHint')}
      onSubmit={(e) => void onSubmit(e)}
      bodyClassName="min-h-0"
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={pending || Boolean(user && canMailbox && detail.isLoading)}>
            {pending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:items-start">
        <div className="space-y-4">
          {!user ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="user-employee">{t('access.directory.form.employee')}</Label>
                <FormSelect
                  id="user-employee"
                  control={form.control}
                  name="employeeId"
                  options={employees.map((e) => ({
                    value: e.id,
                    label: e.name,
                    description: e.workEmail || e.email,
                  }))}
                  placeholder={t('access.directory.form.employeePick')}
                />
                {errors.employeeId ? <p className="text-xs text-destructive">{errors.employeeId.message}</p> : null}
                {selectedEmployee ? (
                  <p className="text-xs text-muted-foreground">
                    {t('access.directory.form.loginEmail')}:{' '}
                    <span className="font-medium text-foreground">{selectedEmployee.workEmail}</span>
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="user-login-password">{t('access.directory.form.loginPassword')}</Label>
                <PasswordInput
                  id="user-login-password"
                  autoComplete="new-password"
                  placeholder={t('access.directory.form.loginPasswordPlaceholder')}
                  aria-invalid={!!errors.password}
                  {...form.register('password')}
                />
                {errors.password ? (
                  <p className="text-xs text-destructive">{errors.password.message}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">{t('access.directory.form.loginPasswordHint')}</p>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="space-y-1">
                <Label>{t('access.directory.form.email')}</Label>
                <p className="text-sm text-muted-foreground">{editing?.email ?? user.email}</p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="user-first">{t('access.directory.form.firstName')}</Label>
                  <Input id="user-first" aria-invalid={!!errors.firstName} {...form.register('firstName')} />
                  {errors.firstName ? <p className="text-xs text-destructive">{errors.firstName.message}</p> : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-last">{t('access.directory.form.lastName')}</Label>
                  <Input id="user-last" aria-invalid={!!errors.lastName} {...form.register('lastName')} />
                  {errors.lastName ? <p className="text-xs text-destructive">{errors.lastName.message}</p> : null}
                </div>
              </div>
            </>
          )}

          {user && canMailbox && detail.isLoading ? <Skeleton className="h-28 rounded-xl" /> : null}

          {showMailbox ? (
            <MailboxPanel
              enabled={mailboxOn}
              onEnabledChange={setMailboxOn}
              providers={providers}
              providerId={form.watch('mailboxProviderId')}
              onProviderId={(v) => form.setValue('mailboxProviderId', v, { shouldValidate: true })}
              secret={form.watch('mailboxSecret')}
              onSecret={(v) => form.setValue('mailboxSecret', v, { shouldValidate: true })}
              signatureHtml={form.watch('mailboxSignature')}
              onSignatureHtml={(v) => form.setValue('mailboxSignature', v)}
              email={user ? form.watch('mailboxEmail') : undefined}
              onEmail={user ? (v) => form.setValue('mailboxEmail', v, { shouldValidate: true }) : undefined}
              existing={user ? mailbox : null}
              remove={mailboxRemove}
              onRemove={user && mailbox ? setMailboxRemove : undefined}
              errors={{
                providerId: errors.mailboxProviderId?.message,
                secret: errors.mailboxSecret?.message,
                email: errors.mailboxEmail?.message,
              }}
            />
          ) : null}
        </div>

        <div className="space-y-2 lg:border-l lg:pl-5">
          <Label>{t('access.directory.form.roles')}</Label>
          <Controller
            control={form.control}
            name="roleIds"
            render={({ field }) => (
              <RoleChecklist roles={roles} value={field.value} onChange={field.onChange} listClassName="max-h-[min(22rem,50vh)]" />
            )}
          />
          {errors.roleIds ? <p className="text-xs text-destructive">{errors.roleIds.message}</p> : null}
        </div>
      </div>
    </Modal>
  )
}
